import React, { useEffect, useState } from "react";
import ConfigurationWidgetCard from "./ConfigurationWidgetCard";
import {
  CONFIGURATION_COMMON_MODELS_PATH,
  MERGE_REDACT_UNMAPPED_DATA_HELP_CENTER,
  getLinkedAccountDetailFieldMappingsPathForId,
} from "../../../../../../../router/RouterUtils";
import { LinkedAccountSettingToggleDropdownOption } from "../../LinkedAccountSettingToggle";
import { toggleRedactUnmappedData } from "../../../../../../../api-client/APIClient";
import { LinkedAccount } from "../../../../../../../models/Entities";
import { Text, Link, Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { ExternalLink } from "lucide-react";

interface Props {
  linkedAccount: LinkedAccount;
}

const RedactUnmappedDataWidgetCard: React.FC<Props> = ({ linkedAccount }: Props) => {
  const [isRedactionEnabledForLinkedAccount, setIsRedactionEnabledForLinkedAccount] = useState<
    boolean | undefined
  >(undefined);

  const isRedactionEnabledForOrganization = linkedAccount.redact_unmapped_data_enabled_for_org;
  const isRedactionEnabled = linkedAccount.should_redact_unmapped_data;

  useEffect(() => {
    setIsRedactionEnabledForLinkedAccount(isRedactionEnabled);
  }, [isRedactionEnabled]);

  const subtitle = (
    <div className="flex flex-col gap-y-2">
      <Text variant="sm" className="text-gray-40">
        Redact all fields in API logs and{" "}
        <Link href={CONFIGURATION_COMMON_MODELS_PATH} target="_blank">
          Remote Data
        </Link>{" "}
        for this Linked Account that are not mapped to Merge Common Model fields or used in{" "}
        <Link href={getLinkedAccountDetailFieldMappingsPathForId(linkedAccount.id)} target="_blank">
          Field Mapping
        </Link>
        .
      </Text>
      <Button
        variant={ButtonVariant.TextBlue}
        href={MERGE_REDACT_UNMAPPED_DATA_HELP_CENTER}
        rightIcon={<ExternalLink size={12} />}
        size="sm"
      >
        Learn more
      </Button>
    </div>
  );

  const _onResponse = (res: { feature_enabled_for_linked_account?: boolean }) => {
    setIsRedactionEnabledForLinkedAccount(res.feature_enabled_for_linked_account ?? undefined);
  };

  const _setRedactionEnabledForLinkedAccount = (
    scopeKey: LinkedAccountSettingToggleDropdownOption,
  ) => {
    switch (scopeKey) {
      case LinkedAccountSettingToggleDropdownOption.ENABLE:
        if (isRedactionEnabledForLinkedAccount !== true)
          toggleRedactUnmappedData(true, undefined, linkedAccount.id, _onResponse);
        break;
      case LinkedAccountSettingToggleDropdownOption.DISABLE:
        if (isRedactionEnabledForLinkedAccount !== false)
          toggleRedactUnmappedData(false, undefined, linkedAccount.id, _onResponse);
        break;
      case LinkedAccountSettingToggleDropdownOption.RESET:
        if (
          isRedactionEnabledForOrganization !== undefined &&
          isRedactionEnabledForLinkedAccount !== isRedactionEnabledForOrganization
        ) {
          toggleRedactUnmappedData(
            isRedactionEnabledForOrganization,
            undefined,
            linkedAccount.id,
            _onResponse,
          );
        }
        break;
    }
  };

  return (
    <ConfigurationWidgetCard
      cardTitle={<Text variant="h6">Redact unmapped data</Text>}
      cardSubtitle={subtitle}
      linkedAccountSettingToggleProps={
        isRedactionEnabledForLinkedAccount !== undefined
          ? {
              onDropdownSelected: _setRedactionEnabledForLinkedAccount,
              isSettingEnabled: isRedactionEnabledForLinkedAccount,
              isDefaultOverridden:
                isRedactionEnabledForLinkedAccount !== isRedactionEnabledForOrganization,
              includeToggleIcon: true,
            }
          : undefined
      }
    />
  );
};

export default RedactUnmappedDataWidgetCard;
